import {
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  forwardRef,
  inject,
  input,
  effect,
  signal, InputSignal, WritableSignal, Signal
} from '@angular/core';
import { takeUntilDestroyed, outputToObservable, toObservable } from '@angular/core/rxjs-interop';
import { merge, switchMap, EMPTY, Observable } from 'rxjs';
import { IdService } from '../../services/id.service';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { ChipComponent } from './chip.component';

import type { ControlValueAccessor } from '@angular/forms';
import type { ChangeEventOn, SelectionChangeEvent } from '../public-api';

/**
 * Chip-list
 * This component will track all its chip component descendants
 * and allow the user to select a single entry or multiple entries
 */
@Component({
  providers: [ValueAccessorProviderFactory(ChipListComponent)],
  selector: 'td-wb-chip-list',
  templateUrl: 'chip-list.component.html',
  styleUrls: ['chip-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChipListComponent implements ControlValueAccessor {
  private readonly idService: IdService = inject(IdService);

  private readonly _internalValue: WritableSignal<unknown> = signal<unknown | Array<unknown> | undefined>(undefined);
  name: InputSignal<string> = input<string>();

  chips: Signal<ReadonlyArray<ChipComponent>> = contentChildren(forwardRef(() => ChipComponent), { descendants: true });
  private readonly chips$: Observable<ReadonlyArray<ChipComponent>> = toObservable(this.chips);

  private readonly optionSelectionChanges$: ChangeEventOn<ChipComponent> = this.chips$.pipe(
    switchMap((chips: ReadonlyArray<ChipComponent>): Observable<SelectionChangeEvent<ChipComponent>> => {
      if (chips?.length) {
        return merge(...chips.map((item: ChipComponent) => outputToObservable(item.action)));
      }
      return EMPTY;
    })
  );

  /**
   * Single Selection.
   * Allows consumers to pick only one or many chip options
   * True (single) by default.
   */
  singleSelection: InputSignal<boolean> = input<boolean>(true);

  constructor() {
    effect(() => {
      this.updateChipIds();
      this.updateChipNames();
      this.updateSelectedChipFromValue();
    });

    this.optionSelectionChanges$.pipe(
      takeUntilDestroyed()
    ).subscribe((event: SelectionChangeEvent<ChipComponent>): void => {
      if (this.singleSelection()) {
        this.value = event.source.selected ? event.source.item() : undefined;
      } else {
        const current = this.value;
        const valArray = Array.isArray(current) ? current : [];
        if (event.source.selected) {
          this.value = [...valArray, event.source.item()];
        } else {
          this.value = valArray.filter((item: unknown) => item !== event.source.item());
        }
      }

      if (event.fromView) {
        this.onTouchCallback();
        this.onChangeCallback(this.value);
      }
    });
  }

  get value(): unknown | Array<unknown> | undefined {
    return this._internalValue();
  }

  set value(newValue: unknown | Array<unknown> | undefined) {
    this._internalValue.set(newValue);
  }

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   *
   * @param value
   */
  writeValue(value: unknown | Array<unknown>): void {
    this.value = value;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(onChangeCallback: (value: unknown | Array<unknown> | undefined) => void): void {
    this.onChangeCallback = onChangeCallback;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(onTouchCallback: () => void): void {
    this.onTouchCallback = onTouchCallback;
  }

  private onChangeCallback: (_: unknown | Array<unknown> | undefined) => void = (): void => {
  };

  private onTouchCallback: () => void = (): void => {
  };

  private updateSelectedChipFromValue(): void {
    const chips = this.chips();
    if (chips !== undefined) {
      if (this.singleSelection()) {
        chips.forEach((chip: ChipComponent): void => {
          chip.setSelected(this.value === chip.item());
        });
      } else { //multi
        const valueArray = Array.isArray(this.value) ? this.value : [];
        chips.forEach((chip: ChipComponent): void => {
          chip.setSelected(valueArray.includes(chip.item()));
        });
      }
    }
  }

  private updateChipNames(): void {
    const chips = this.chips();
    if (chips !== undefined) {
      chips.forEach((chip: ChipComponent): void => {
        if (this.name() !== undefined) {
          chip.name.set(this.name());
        } else {
          chip.name.set(chip.id());
        }
      });
    }
  }

  private updateChipIds(): void {
    const chips = this.chips();
    if (chips !== undefined) {
      chips.forEach((chip: ChipComponent): void => {
        if (chip.id() === undefined) {
          chip.id.set(this.getRadioId());
        }
      });
    }
  }

  private getRadioId(): string {
    return this.idService.generateNamespaceSequentialId('chips');
  }
}
